import { cacheVerifiedSession, readVerifiedSession, clearVerifiedSession, sessionClaims } from "@/lib/verified-session";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { boundedFetch } from "@/lib/bounded-fetch";
import { setQueueScope } from "@/lib/offline-queue";
import { apiUrl } from "@/lib/api-config";

const TOKEN_KEY = "compostdeveloper_token";
/** When VITE_DISABLE_AUTH is on, logout sets this so refresh does not auto-restore the dev user. */
const DEV_SIGNED_OUT_KEY = "cd_dev_signed_out";

function devSignedOut(): boolean {
  try {
    return sessionStorage.getItem(DEV_SIGNED_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

function markDevSignedOut() {
  try {
    sessionStorage.setItem(DEV_SIGNED_OUT_KEY, "1");
  } catch {
    /* ignore */
  }
}

function clearDevSignedOut() {
  try {
    sessionStorage.removeItem(DEV_SIGNED_OUT_KEY);
  } catch {
    /* ignore */
  }
}

/** Set to true or use VITE_DISABLE_AUTH=true in .env to bypass login and work on UI. */
const AUTH_DISABLED = import.meta.env.VITE_DISABLE_AUTH === "true";

export type User = { id: string; email: string; name: string; role?: string; features?: string[]; organization_id?: number | null };

const DEV_USER: User = { id: "dev", email: "test@compostdeveloper.com", name: "Dev User", features: ["composting", "operations", "admin", "platform_admin"] };

type AuthContextValue = {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (input: { name: string; email: string; password: string; role?: string; company?: string }) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateToken: (newToken: string) => void;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function initialUser(): User | null {
  if (!AUTH_DISABLED) return null;
  if (devSignedOut()) return null;
  return DEV_USER;
}

function initialToken(): string | null {
  if (!AUTH_DISABLED) return localStorage.getItem(TOKEN_KEY);
  if (devSignedOut()) return null;
  return "dev-bypass";
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(initialUser);
  const [token, setToken] = useState<string | null>(initialToken);
  const [loading, setLoading] = useState(!AUTH_DISABLED);

  const [sessionError, setSessionError] = useState(false);
  const [sessionAttempt, setSessionAttempt] = useState(0);
  const logout = useCallback(() => {
    setQueueScope(null);
    clearVerifiedSession();
    setSessionError(false);
    localStorage.removeItem(TOKEN_KEY);
    if (AUTH_DISABLED) markDevSignedOut();
    setToken(null);
    setUser(null);
  }, []);

  const updateToken = useCallback((newToken: string) => {
    if (AUTH_DISABLED) clearDevSignedOut();
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
  }, []);

  const register = useCallback(
    async (input: { name: string; email: string; password: string; role?: string; company?: string }) => {
      try {
        const res = await boundedFetch(apiUrl("/api/register"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        });
        const text = await res.text();
        const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
        if (!res.ok) {
          return { success: false, error: data.error || `Request failed (${res.status})` };
        }
        if (!data.success) {
          return { success: false, error: data.error || "Registration failed" };
        }
        if (AUTH_DISABLED) clearDevSignedOut();
        localStorage.setItem(TOKEN_KEY, data.token);
        setToken(data.token);
        setUser(data.user);
        return { success: true };
      } catch {
        return {
          success: false,
          error: "Can't reach the server. Please check your internet connection and try again.",
        };
      }
    },
    []
  );

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await boundedFetch(apiUrl("/api/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const text = await res.text();
      const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok) {
        return { success: false, error: data.error || `Request failed (${res.status})` };
      }
      if (!data.success) {
        return { success: false, error: data.error || "Login failed" };
      }
      if (AUTH_DISABLED) clearDevSignedOut();
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setUser(data.user);
      return { success: true };
    } catch (e) {
      return {
        success: false,
        error: "Can't reach the server. Please check your internet connection and try again.",
      };
    }
  }, []);

  useEffect(() => {
    if (AUTH_DISABLED) {
      setLoading(false);
      return;
    }
    if (!token) {
      setLoading(false);
      return;
    }
    if (!navigator.onLine) {
      const cached = readVerifiedSession(token);
      if (cached) { setUser(cached); setSessionError(true); setLoading(false); }
    }
    let cancelled = false;
    boundedFetch(apiUrl("/api/me"), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) return res.json();
        if (res.status === 401) { logout(); return; }
        throw new Error('Session verification unavailable');
      })
      .then((data) => {
        if (cancelled || !data?.user) return;
        setSessionError(false);
        const verifiedUser = { ...data.user, organization_id: data.organization?.id ?? data.user.organization_id ?? null };
        cacheVerifiedSession(token, verifiedUser);
        setUser(verifiedUser);
      })
      .catch(() => {
        if (!cancelled) {
          const cached = readVerifiedSession(token);
          if (cached) setUser(cached);
          setSessionError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, logout, sessionAttempt]);

  useEffect(() => {
    const retry = () => setSessionAttempt(value => value + 1);
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, []);

  useEffect(() => {
    if (!token || AUTH_DISABLED) return;
    const claims = sessionClaims(token);
    if (!claims) return;
    const remaining = claims.expiresAt - Date.now();
    if (remaining <= 0) { logout(); return; }
    const timer = setTimeout(logout, Math.min(remaining, 2147483647));
    return () => clearTimeout(timer);
  }, [token, logout]);

  const value: AuthContextValue = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateToken,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>
    {sessionError && user && <div role="status" className="bg-amber-100 text-amber-950 px-4 py-2 text-sm">Using your last verified session. Entries save on this device; server access will be checked when connected.</div>}
    {sessionError && token && !user ? <main className="min-h-screen flex items-center justify-center p-6"><div role="alert" className="max-w-sm space-y-4"><h1 className="text-xl font-semibold">Unable to verify your session</h1><p>Your login has been kept. Check your connection and try again.</p><button className="rounded border px-4 py-3" onClick={() => { setLoading(true); setSessionAttempt(value => value + 1); }}>Try again</button><button className="ml-3 px-4 py-3" onClick={logout}>Sign out</button></div></main> : children}
  </AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
